import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { isEmptyArray, isNullOrUndefined } from "src/common/utils";
import { useQuery } from "../../../common/hooks";
import { Routes } from "../../../common/Routes";
import {
  InformationAssetsAction,
  sendInformationAssetsEvent,
} from "../../../components/matomoClient";
import { AppSideBarPageActions, SideBarOptions } from "../../../parts/AppSideBar/model";
import { useFetchApisStatusForInformationAssetsPage } from "../hooks/api-progress/information-asset-page";
import { useInformationAssetListRecordGenerator } from "../hooks/queries/resources";
import { InformationAssetDetailProps } from "./index";

class SystemResourceTypeTab {
  id: string;
  selectedSystem: string;
  selectedResourceType: string;
  group: string | null;

  constructor(selectedSystem: string, selectedResourceType: string, group: string | null) {
    this.id = `${selectedSystem} - ${selectedResourceType}`;
    this.selectedResourceType = selectedResourceType;
    this.selectedSystem = selectedSystem;
    this.group = group;
  }

  link(assetId: string): string {
    const link = `${Routes.InformationAssets.path}/${assetId}/systemResourceType?selectedSystem=${this.selectedSystem}&selectedResourceType=${this.selectedResourceType}`;
    if (this.group) {
      return link + `&group=${encodeURIComponent(this.group)}`;
    } else {
      return link;
    }
  }
}

export const useInformationAssetDetailController = (props: InformationAssetDetailProps) => {
  const assetId = props.match.params.assetId;

  const query = useQuery();
  const queriedSelectedSystem = query.get("selectedSystem");
  const queriedSelectedResourceType = query.get("selectedResourceType");
  const queriedGroupToBeFiltered = query.get("group");

  const resourceAndSystemResourceMapping = useInformationAssetListRecordGenerator();

  const dispatch = useDispatch();
  const history = useHistory();
  const informationAssetPageApiCallStatus = useFetchApisStatusForInformationAssetsPage();
  const availableTabs = ["accessDetails", "systemResourceType"];

  const [systemResourceTypeTabs, setSystemResourceTypeTabs] = useState<
    Array<SystemResourceTypeTab>
  >([]);

  useEffect(() => {
    if (!informationAssetPageApiCallStatus.isApiCalled) {
      informationAssetPageApiCallStatus.dispatchApiCall(false);
    }
    dispatch(AppSideBarPageActions.updateSelectedSideBar(SideBarOptions.INFORMATION_ASSETS));
  }, [informationAssetPageApiCallStatus.isApiCalled]);

  function systemResourceTypeIdentifier() {
    if (queriedSelectedSystem && queriedSelectedResourceType) {
      return `${queriedSelectedSystem} - ${queriedSelectedResourceType}`;
    }
  }

  const getInformationAsset = () =>
    resourceAndSystemResourceMapping?.find((informationAsset) => informationAsset.id === assetId);

  function backButtonClickFunctionality() {
    history.push(Routes.InformationAssets.path);
    sendInformationAssetsEvent(InformationAssetsAction.BackToInformationAssets);
  }

  const getSystemResources = () => {
    return getInformationAsset()
      ? // @ts-ignore
        getInformationAsset()?.associatedSystemResources.filter(
          (systemResource) =>
            systemResource.system.name === queriedSelectedSystem &&
            systemResource.type === queriedSelectedResourceType
        )
      : null;
  };
  const correctSystemAndSystemResourceType = () => {
    return !isEmptyArray(getSystemResources() ?? []);
  };

  function defaultActiveTab() {
    const selectedTabType = props.match.params.activeTab;
    if (
      selectedTabType === availableTabs[1] &&
      queriedSelectedSystem &&
      queriedSelectedResourceType &&
      correctSystemAndSystemResourceType()
    ) {
      return systemResourceTypeIdentifier() ?? availableTabs[0];
    } else {
      return "accessDetails";
    }
  }

  function manageTabs(): void {
    if (
      queriedSelectedResourceType &&
      queriedSelectedSystem &&
      props.match.params.activeTab === availableTabs[1] &&
      correctSystemAndSystemResourceType()
    ) {
      const foundSystemResourceTypeTab = systemResourceTypeTabs.find(
        (systemResourceTypeTab) => systemResourceTypeTab.id === systemResourceTypeIdentifier()
      );
      if (isNullOrUndefined(foundSystemResourceTypeTab)) {
        setSystemResourceTypeTabs([
          ...systemResourceTypeTabs,
          new SystemResourceTypeTab(
            queriedSelectedSystem,
            queriedSelectedResourceType,
            queriedGroupToBeFiltered
          ),
        ]);
      } else if (foundSystemResourceTypeTab?.group !== queriedGroupToBeFiltered) {
        const foundSystemResourceIndex = systemResourceTypeTabs.findIndex(
          (systemResourceTypeTab) => systemResourceTypeTab.id === systemResourceTypeIdentifier()
        );
        const clonedSystemResourceTypeTabs = [...systemResourceTypeTabs];
        clonedSystemResourceTypeTabs[foundSystemResourceIndex].group = queriedGroupToBeFiltered;
        setSystemResourceTypeTabs(clonedSystemResourceTypeTabs);
      }
      setActiveTab(systemResourceTypeIdentifier() ?? availableTabs[0]);
    } else if (props.match.params.activeTab === availableTabs[0]) {
      navigateToActiveTab(availableTabs[0]);
    }
  }

  useEffect(manageTabs, [
    queriedSelectedSystem,
    queriedSelectedResourceType,
    queriedGroupToBeFiltered,
    correctSystemAndSystemResourceType(),
    props.match.params.activeTab,
  ]);

  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab());

  function navigateToActiveTab(tabId: string) {
    const foundSystemResourceTypeTab = systemResourceTypeTabs.find(
      (systemResourceTypeTab) => systemResourceTypeTab.id === tabId
    );
    setActiveTab(tabId);
    if (foundSystemResourceTypeTab) {
      history.push(foundSystemResourceTypeTab.link(assetId));
    } else {
      history.push(`${Routes.InformationAssets.path}/${assetId}/accessDetails`);
    }
  }

  return {
    getInformationAsset,
    shouldRenderAssetDetail: () => !!getInformationAsset(),
    shouldRenderProgress: () => informationAssetPageApiCallStatus.isApiCallLoading,
    isAssetIdInValid:
      !informationAssetPageApiCallStatus.isApiCallFailed &&
      informationAssetPageApiCallStatus.isApiCallCompletedSuccessfully &&
      isNullOrUndefined(getInformationAsset()),
    backButtonClickFunctionality,
    getSystemResources,
    getActiveTab: () => activeTab,
    setActiveTab: (activeKey: string) => {
      navigateToActiveTab(activeKey);
      sendInformationAssetsEvent(
        InformationAssetsAction.TabChange(
          activeKey === "accessDetails"
            ? getInformationAsset()?.name.toString() + " Access Details"
            : queriedSelectedResourceType
        )
      );
    },
    isErrorLoadingApis: informationAssetPageApiCallStatus.isApiCallFailed,
    tabOnEdit: (targetKey: string, action: "add" | "remove") => {
      if (action === "remove") {
        setSystemResourceTypeTabs(
          systemResourceTypeTabs.filter(
            (systemResourceTypeTab) => systemResourceTypeTab.id !== targetKey
          )
        );
        if (props.match.params.activeTab !== availableTabs[0])
          navigateToActiveTab(availableTabs[0]);
      }
    },
    systemResourceTypeTabs,
  };
};
